import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  EventEmitter,
  forwardRef,
  signal,
  computed,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

export type DatePickerMode = 'single' | 'range';
export type TimeFormat = '12h' | '24h';
export type DatePickerValue = Date | DateRange | null;
export type DatePickerInput = Date | DateRange | string | null;

export interface DateRange {
  start: Date;
  end: Date | null;
}

export interface CalendarDay {
  date: Date;
  inCurrentMonth: boolean;
  isToday: boolean;
}

@Component({
  selector: 'app-advanced-datepicker',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './advanced-datepicker.component.html',
  styleUrl: './advanced-datepicker.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AdvancedDatepickerComponent),
      multi: true,
    },
  ],
})
export class AdvancedDatepickerComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  private readonly translateService = inject(TranslateService);

  @ViewChild('inputRef', { static: false })
  inputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('popupRef', { static: false })
  popupRef!: ElementRef<HTMLDivElement>;
  @ViewChild('iconButtonRef', { static: false })
  iconButtonRef!: ElementRef<HTMLButtonElement>;

  @Input() mode: DatePickerMode = 'single';
  @Input() showTime: boolean = false;
  /** Time picker only; hides calendar and displays time in the input. */
  @Input() timeOnly: boolean = false;
  @Input() showSeconds: boolean = false;
  @Input() dateFormat: string = 'DD/MM/YYYY';
  @Input() timeFormat: TimeFormat = '24h';
  @Input() placeholder: string = '';
  @Input() minDate?: Date | null;
  @Input() maxDate?: Date | null;
  @Input() disabled: boolean = false;
  /** Render popup on document.body so it is not clipped by dialog overflow. */
  @Input() appendToBody: boolean = false;

  @Output() dateChange = new EventEmitter<DatePickerValue>();

  // Internal state
  isOpen = signal(false);
  currentView = signal<'calendar' | 'month-year'>('calendar');
  currentMonth = signal(new Date().getMonth());
  currentYear = signal(new Date().getFullYear());
  hoveredDate = signal<Date | null>(null);
  currentLang = signal<string>('en');
  readonly bodyPopupStyle = signal<Record<string, string>>({});
  private bodyPopupEl: HTMLElement | null = null;
  private bodyPopupHome: HTMLElement | null = null;

  // Value state
  private readonly _value = signal<DatePickerValue>(null);
  private _onChange: ((value: DatePickerValue) => void) | null = null;
  private _onTouched: (() => void) | null = null;

  // Computed values
  calendarDays = computed(() => this.generateCalendarGrid());
  displayValue = computed(() => this.formatDisplayValue());
  weekStartDay = computed(() => {
    return this.currentLang() === 'vi' ? 1 : 0; // Monday for Vietnamese, Sunday for English
  });
  dayNames = computed(() => {
    const startDay = this.weekStartDay();
    const days = [];

    for (let i = 0; i < 7; i++) {
      const dayIndex = (startDay + i) % 7;
      const key = `common.datepicker.days.${dayIndex}`;
      days.push(this.translateService.instant(key));
    }

    return days;
  });
  monthYearLabel = computed(() => {
    const monthKey = `common.datepicker.months.${this.currentMonth()}`;
    const monthName = this.translateService.instant(monthKey);
    return `${monthName} ${this.currentYear()}`;
  });
  monthNames = computed(() => {
    const months = [];
    for (let i = 0; i < 12; i++) {
      const key = `common.datepicker.months.${i}`;
      months.push(this.translateService.instant(key));
    }
    return months;
  });
  timeLabel = computed(() => {
    return this.translateService.instant('common.datepicker.time');
  });
  fromLabel = computed(() => {
    return this.translateService.instant('common.datepicker.from');
  });
  toLabel = computed(() => {
    return this.translateService.instant('common.datepicker.to');
  });
  selectDatePlaceholder = computed(() => {
    return this.translateService.instant('common.datepicker.select_date');
  });
  todayLabel = computed(() => {
    return this.translateService.instant('common.datepicker.today');
  });
  resetLabel = computed(() => {
    return this.translateService.instant('common.datepicker.reset');
  });

  // Time picker state
  startHour = signal(0);
  startMinute = signal(0);
  startSecond = signal(0);
  startAmPm = signal<'AM' | 'PM'>('AM');
  endHour = signal(0);
  endMinute = signal(0);
  endSecond = signal(0);
  endAmPm = signal<'AM' | 'PM'>('AM');

  ngOnInit(): void {
    // Get initial language - try to get from service, fallback to 'en'
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const initialLang = (this.translateService as any)['currentLang'] ?? 'en';
    this.currentLang.set(initialLang);
    this.initializeFromValue();

    // Subscribe to language changes
    this.translateService.onLangChange.subscribe((event) => {
      this.currentLang.set(event.lang);
    });
  }

  // ControlValueAccessor implementation
  writeValue(value: DatePickerInput): void {
    const normalizedValue = this.normalizeValue(value);
    this._value.set(normalizedValue);
    this.initializeFromValue();
  }

  registerOnChange(fn: (value: DatePickerValue) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Calendar logic
  generateCalendarGrid(): CalendarDay[] {
    const year = this.currentYear();
    const month = this.currentMonth();
    const firstDay = new Date(year, month, 1);
    const weekStart = this.weekStartDay(); // 0 for Sunday, 1 for Monday

    // Get the first day of the month's day of week (0 = Sunday, 1 = Monday, etc.)
    let firstDayOfWeek = firstDay.getDay();

    // Adjust for week start day
    // If week starts on Monday (weekStart = 1), shift Sunday (0) to 6
    if (weekStart === 1) {
      firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    }

    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDayOfWeek);

    const days: CalendarDay[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      const dateCopy = new Date(date);
      dateCopy.setHours(0, 0, 0, 0);

      days.push({
        date,
        inCurrentMonth: date.getMonth() === month,
        isToday: dateCopy.getTime() === today.getTime(),
      });
    }

    return days;
  }

  // Date selection
  selectDate(day: CalendarDay): void {
    if (this.isDateDisabled(day.date)) {
      return;
    }

    const selectedDate = new Date(day.date);
    if (this.showTime) {
      selectedDate.setHours(
        this.startHour(),
        this.startMinute(),
        this.startSecond(),
        0,
      );
    } else {
      selectedDate.setHours(0, 0, 0, 0);
    }

    if (this.mode === 'single') {
      this._value.set(selectedDate);
      this.updateValue();
      this.closePopup();
    } else {
      this.handleRangeSelection(selectedDate);
    }
  }

  private handleRangeSelection(selectedDate: Date): void {
    const currentRange = this._value() as DateRange | null;

    if (!currentRange?.start || currentRange?.end) {
      // Start new range
      this._value.set({ start: selectedDate, end: null });
      this.updateTimeForRange('start', selectedDate);
    } else {
      // Complete range
      let start = currentRange.start;
      let end = selectedDate;

      if (end < start) {
        [start, end] = [end, start];
      }

      if (this.showTime) {
        end.setHours(this.endHour(), this.endMinute(), this.endSecond(), 0);
      } else {
        end.setHours(23, 59, 59, 999);
      }

      this._value.set({ start, end });
      this.updateValue();
      this.closePopup();
    }
  }

  // Navigation
  previousMonth(): void {
    if (this.currentView() === 'calendar') {
      if (this.currentMonth() === 0) {
        this.currentMonth.set(11);
        this.currentYear.update((y) => y - 1);
      } else {
        this.currentMonth.update((m) => m - 1);
      }
    } else {
      this.currentYear.update((y) => y - 1);
    }
  }

  nextMonth(): void {
    if (this.currentView() === 'calendar') {
      if (this.currentMonth() === 11) {
        this.currentMonth.set(0);
        this.currentYear.update((y) => y + 1);
      } else {
        this.currentMonth.update((m) => m + 1);
      }
    } else {
      this.currentYear.update((y) => y + 1);
    }
  }

  toggleMonthYearView(): void {
    this.currentView.update((view) =>
      view === 'calendar' ? 'month-year' : 'calendar',
    );
  }

  selectMonth(monthIndex: number): void {
    this.currentMonth.set(monthIndex);
    this.currentView.set('calendar');
  }

  // Time picker
  updateTimeForRange(type: 'start' | 'end', date: Date): void {
    if (type === 'start') {
      this.startHour.set(date.getHours());
      this.startMinute.set(date.getMinutes());
      this.startSecond.set(date.getSeconds());
      if (this.timeFormat === '12h') {
        const hour = date.getHours();
        this.startHour.set(hour % 12 || 12);
        this.startAmPm.set(hour >= 12 ? 'PM' : 'AM');
      }
    } else {
      this.endHour.set(date.getHours());
      this.endMinute.set(date.getMinutes());
      this.endSecond.set(date.getSeconds());
      if (this.timeFormat === '12h') {
        const hour = date.getHours();
        this.endHour.set(hour % 12 || 12);
        this.endAmPm.set(hour >= 12 ? 'PM' : 'AM');
      }
    }
  }

  onTimeChange(): void {
    const value = this._value();
    if (!value) {
      this.applyTimeToNullValue();
      return;
    }
    if (this.mode === 'single') {
      this.applyTimeToSingleValue(value as Date);
    } else {
      this.applyTimeToRangeValue(value as DateRange);
    }
  }

  private applyTimeToNullValue(): void {
    if (this.mode === 'single') {
      this._value.set(this.createDraftDateFromTime('start'));
    } else {
      this._value.set({
        start: this.createDraftDateFromTime('start'),
        end: null,
      });
    }
    this.updateValue();
  }

  private resolveHours(rawHours: number, amPm: 'AM' | 'PM'): number {
    if (this.timeFormat !== '12h') {
      return rawHours;
    }
    return amPm === 'PM' ? (rawHours % 12) + 12 : rawHours % 12;
  }

  private applyTimeToSingleValue(date: Date): void {
    const hours = this.resolveHours(this.startHour(), this.startAmPm());
    const next = new Date(date);
    next.setHours(hours, this.startMinute(), this.startSecond(), 0);
    this._value.set(next);
    this.updateValue();
  }

  private applyTimeToRangeValue(range: DateRange): void {
    if (range.start) {
      const startHours = this.resolveHours(this.startHour(), this.startAmPm());
      const start = new Date(range.start);
      start.setHours(startHours, this.startMinute(), this.startSecond(), 0);
      range.start = start;
    }
    if (range.end) {
      const endHours = this.resolveHours(this.endHour(), this.endAmPm());
      const end = new Date(range.end);
      end.setHours(endHours, this.endMinute(), this.endSecond(), 0);
      range.end = end;
    }
    this._value.set({ start: range.start ?? null, end: range.end ?? null });
    this.updateValue();
  }

  // Formatting
  formatDisplayValue(): string {
    const value = this._value();
    if (!value) {
      return '';
    }
    if (this.mode === 'single') {
      return this.formatSingleDisplayValue(value as Date);
    }
    return this.formatRangeDisplayValue(value as DateRange);
  }

  private formatSingleDisplayValue(date: Date): string {
    if (this.timeOnly) {
      return this.formatTime(date);
    }
    const dateText = this.formatDate(date);
    return this.showTime
      ? `${dateText} ${this.formatTime(date)}`.trim()
      : dateText;
  }

  private usesTimePicker(): boolean {
    return this.showTime || this.timeOnly;
  }

  private formatRangeDisplayValue(range: DateRange): string {
    if (!range.start) {
      return '';
    }
    if (!range.end) {
      const startText = this.formatDate(range.start);
      return this.showTime
        ? `${startText} ${this.formatTime(range.start)}`.trim()
        : startText;
    }
    const startText = this.showTime
      ? `${this.formatDate(range.start)} ${this.formatTime(range.start)}`
      : this.formatDate(range.start);
    const endText = this.showTime
      ? `${this.formatDate(range.end)} ${this.formatTime(range.end)}`
      : this.formatDate(range.end);
    return `${startText} - ${endText}`;
  }

  formatDate(date: Date | string | null): string {
    if (!date) {
      return '';
    }

    let dateObj: Date;
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      const parsed = this.parseStringDate(date);
      if (!parsed) {
        return '';
      }
      dateObj = parsed;
    } else {
      return '';
    }

    if (Number.isNaN(dateObj.getTime())) {
      return '';
    }

    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const day = dateObj.getDate().toString().padStart(2, '0');
    const year = dateObj.getFullYear();

    return this.dateFormat
      .replace('MM', month)
      .replace('DD', day)
      .replace('YYYY', year.toString());
  }

  formatTime(date: Date): string {
    if (!date) {
      return '';
    }

    let hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    if (this.timeFormat === '12h') {
      const amPm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      const base = `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}`;
      const withSeconds = this.showSeconds
        ? `${base}:${seconds.toString().padStart(2, '0')}`
        : base;
      return `${withSeconds} ${amPm}`;
    }

    const base = `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}`;
    if (!this.showSeconds) {
      return base;
    }
    return `${base}:${seconds.toString().padStart(2, '0')}`;
  }

  // Date state checks
  isDateSelected(day: CalendarDay): boolean {
    const value = this._value();
    if (!value) {
      return false;
    }

    const dayTime = new Date(day.date).setHours(0, 0, 0, 0);

    if (this.mode === 'single') {
      const valueTime = new Date(value as Date).setHours(0, 0, 0, 0);
      return dayTime === valueTime;
    } else {
      const range = value as DateRange;
      if (!range.start) {
        return false;
      }
      const startTime = new Date(range.start).setHours(0, 0, 0, 0);
      if (range.end) {
        const endTime = new Date(range.end).setHours(0, 0, 0, 0);
        return dayTime >= startTime && dayTime <= endTime;
      }
      return dayTime === startTime;
    }
  }

  isDateInRange(day: CalendarDay): boolean {
    const value = this._value();
    if (this.mode !== 'range' || !value) {
      return false;
    }

    const range = value as DateRange;
    if (!range.start || !range.end) {
      return false;
    }

    const dayTime = new Date(day.date).setHours(0, 0, 0, 0);
    const startTime = new Date(range.start).setHours(0, 0, 0, 0);
    const endTime = new Date(range.end).setHours(0, 0, 0, 0);

    return dayTime > startTime && dayTime < endTime;
  }

  isDateStart(day: CalendarDay): boolean {
    const value = this._value();
    if (this.mode !== 'range' || !value) {
      return false;
    }

    const range = value as DateRange;
    if (!range.start) {
      return false;
    }

    const dayTime = new Date(day.date).setHours(0, 0, 0, 0);
    const startTime = new Date(range.start).setHours(0, 0, 0, 0);

    return dayTime === startTime;
  }

  isDateEnd(day: CalendarDay): boolean {
    const value = this._value();
    if (this.mode !== 'range' || !value) {
      return false;
    }

    const range = value as DateRange;
    if (!range.end) {
      return false;
    }

    const dayTime = new Date(day.date).setHours(0, 0, 0, 0);
    const endTime = new Date(range.end).setHours(0, 0, 0, 0);

    return dayTime === endTime;
  }

  isDateDisabled(date: Date): boolean {
    const dateTime = new Date(date).setHours(0, 0, 0, 0);

    if (this.minDate) {
      const minTime = new Date(this.minDate).setHours(0, 0, 0, 0);
      if (dateTime < minTime) {
        return true;
      }
    }

    if (this.maxDate) {
      const maxTime = new Date(this.maxDate).setHours(0, 0, 0, 0);
      if (dateTime > maxTime) {
        return true;
      }
    }

    return false;
  }

  getHoverPreviewEnd(): Date | null {
    const value = this._value();
    if (this.mode !== 'range' || !value || !this.hoveredDate()) {
      return null;
    }

    const range = value as DateRange;
    if (!range.start || range.end) {
      return null;
    }

    const hovered = this.hoveredDate()!;
    const start = range.start;

    if (hovered < start) {
      return start;
    }

    return hovered;
  }

  get value(): DatePickerValue {
    return this._value();
  }

  isDateInHoverRange(day: CalendarDay): boolean {
    const value = this._value();
    if (this.mode !== 'range' || !value || !this.hoveredDate()) {
      return false;
    }

    const range = value as DateRange;
    if (!range.start || range.end) {
      return false;
    }

    const hovered = this.hoveredDate()!;
    const dayTime = new Date(day.date).setHours(0, 0, 0, 0);
    const startTime = new Date(range.start).setHours(0, 0, 0, 0);
    const hoverTime = new Date(hovered).setHours(0, 0, 0, 0);

    if (hoverTime < startTime) {
      return dayTime >= hoverTime && dayTime < startTime;
    }

    return dayTime > startTime && dayTime <= hoverTime;
  }

  getDayClasses(day: CalendarDay): string {
    const classes: string[] = [
      'w-10',
      'h-10',
      'flex',
      'items-center',
      'justify-center',
      'text-sm',
      'font-medium',
      'transition-colors',
    ];

    const isSelected =
      this.isDateSelected(day) &&
      (this.mode === 'single' || this.isDateStart(day) || this.isDateEnd(day));
    const value = this._value();
    const isInRange =
      this.isDateInRange(day) ||
      (this.mode === 'range' &&
        value &&
        !(value as DateRange).end &&
        this.hoveredDate() &&
        this.isDateInHoverRange(day));
    const isToday = day.isToday && !isSelected;

    if (isSelected) {
      classes.push('bg-gray-100', 'text-gray-700', 'rounded-full');
    } else if (isInRange) {
      classes.push('bg-sky-100', 'text-sky-700', 'rounded-none');
    } else if (isToday) {
      classes.push('bg-gray-200', 'text-gray-800', 'rounded-full');
    } else {
      classes.push('rounded-full');
      if (day.inCurrentMonth) {
        classes.push('hover:bg-gray-100');
      } else {
        classes.push('text-gray-300');
      }
    }

    if (this.isDateDisabled(day.date)) {
      classes.push('cursor-not-allowed', 'opacity-50');
    }

    return classes.join(' ');
  }

  ngOnDestroy(): void {
    this.detachBodyPopup();
  }

  // Popup management
  openPopup(): void {
    if (this.disabled) {
      return;
    }
    this.isOpen.set(true);
    this.initializeFromValue();
    if (this.appendToBody) {
      setTimeout(() => this.attachBodyPopup(), 0);
    }
  }

  // Icon button click handler
  onIconClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (!this.disabled) {
      // Use setTimeout to ensure popup opens after click event completes
      setTimeout(() => {
        this.inputRef?.nativeElement?.focus();
        this.openPopup();
      }, 0);
    }
  }

  closePopup(): void {
    this.detachBodyPopup();
    this.isOpen.set(false);
    this.currentView.set('calendar');
    this.hoveredDate.set(null);
    // Don't mark as touched here - let onInputBlur handle it
    // This prevents marking as touched when popup closes via outside click
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onWindowReposition(): void {
    if (this.appendToBody && this.isOpen()) {
      this.repositionBodyPopup();
    }
  }

  private attachBodyPopup(): void {
    const popup = this.popupRef?.nativeElement;
    if (!popup || !this.appendToBody) {
      return;
    }
    this.bodyPopupEl = popup;
    this.bodyPopupHome = popup.parentElement;
    document.body.appendChild(popup);
    this.repositionBodyPopup();
  }

  private detachBodyPopup(): void {
    if (this.bodyPopupEl && this.bodyPopupHome && this.bodyPopupEl.parentElement === document.body) {
      this.bodyPopupHome.appendChild(this.bodyPopupEl);
    }
    this.bodyPopupEl = null;
    this.bodyPopupHome = null;
    this.bodyPopupStyle.set({});
  }

  private repositionBodyPopup(): void {
    const input = this.inputRef?.nativeElement;
    if (!input || !this.appendToBody) {
      return;
    }
    const rect = input.getBoundingClientRect();
    const width = Math.max(rect.width, 320);
    let left = rect.left;
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8);
    }
    const top = rect.bottom + 4;
    this.bodyPopupStyle.set({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      width: `${width}px`,
      zIndex: '12000',
      marginTop: '0',
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    // Check if click is outside the datepicker component
    if (
      this.isOpen() &&
      !this.popupRef?.nativeElement?.contains(target) &&
      !this.inputRef?.nativeElement?.contains(target) &&
      !this.iconButtonRef?.nativeElement?.contains(target)
    ) {
      // Use setTimeout to ensure this runs after any icon click handlers
      setTimeout(() => {
        if (this.isOpen()) {
          this.closePopup();
        }
      }, 0);
    }
  }

  // Input handling
  onInputBlur(event: FocusEvent): void {
    // Don't process blur if the focus is moving to the popup or icon button
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (
      relatedTarget &&
      (this.popupRef?.nativeElement?.contains(relatedTarget) ||
        this.iconButtonRef?.nativeElement?.contains(relatedTarget))
    ) {
      return;
    }

    // Mark as touched when user blurs the input field
    // This ensures validation errors only show after user interaction
    if (this._onTouched) {
      this._onTouched();
    }

    // Use setTimeout to allow click events on popup to process first
    setTimeout(() => {
      if (!this.isOpen()) {
        return;
      }
      const inputValue = this.inputRef?.nativeElement?.value || '';
      const parsed = this.parseInputValue(inputValue);
      if (parsed) {
        this._value.set(parsed);
        this.updateValue();
      }
    }, 150);
  }

  private parseInputValue(value: string): DatePickerValue {
    if (!value.trim()) {
      return null;
    }

    if (this.mode === 'single') {
      const date = this.parseDate(value);
      return date && !Number.isNaN(date.getTime()) ? date : null;
    } else {
      const parts = value.split(' - ');
      if (parts.length === 2) {
        const start = this.parseDate(parts[0].trim());
        const end = this.parseDate(parts[1].trim());
        if (
          start &&
          end &&
          !Number.isNaN(start.getTime()) &&
          !Number.isNaN(end.getTime())
        ) {
          return { start, end };
        }
      }
      return null;
    }
  }

  private parseDate(value: string): Date | null {
    const regex = /(\d{1,2})\/(\d{1,2})\/(\d{4})/;
    const match = regex.exec(value);

    if (!match) {
      return null;
    }

    const isDayFirst =
      this.dateFormat.indexOf('DD') < this.dateFormat.indexOf('MM');
    const day = Number.parseInt(isDayFirst ? match[1] : match[2], 10);
    const month = Number.parseInt(isDayFirst ? match[2] : match[1], 10) - 1;
    const year = Number.parseInt(match[3], 10);

    return new Date(year, month, day);
  }

  // Helpers
  private normalizeValue(value: DatePickerInput): DatePickerValue {
    if (!value) {
      return null;
    }

    if (this.mode === 'single') {
      return this.normalizeSingleValue(value);
    } else {
      return this.normalizeRangeValue(value);
    }
  }

  private normalizeSingleValue(value: DatePickerInput): Date | null {
    if (typeof value === 'string') {
      return this.parseStringDate(value);
    }
    if (value instanceof Date) {
      return value;
    }
    return null;
  }

  private normalizeRangeValue(value: DatePickerInput): DateRange | null {
    if (typeof value === 'string') {
      const parts = value.split(' - ');
      if (parts.length === 2) {
        const start = this.parseStringDate(parts[0].trim());
        const end = this.parseStringDate(parts[1].trim());
        if (start && end) {
          return { start, end };
        }
      } else {
        const date = this.parseStringDate(value);
        if (date) {
          return { start: date, end: null };
        }
      }
      return null;
    }
    if (value && typeof value === 'object' && 'start' in value) {
      const normalizedEnd = this.normalizeRangeEnd(value.end);
      return {
        start:
          value.start instanceof Date
            ? value.start
            : this.parseStringDate(String(value.start)) || new Date(),
        end: normalizedEnd,
      };
    }
    return null;
  }

  private normalizeRangeEnd(end: Date | null | undefined): Date | null {
    if (!end) {
      return null;
    }
    return end instanceof Date ? end : this.parseStringDate(String(end));
  }

  private parseStringDate(value: string): Date | null {
    if (!value || typeof value !== 'string') {
      return null;
    }

    // Try YYYY-MM-DD format (from purchase order form)
    const isoRegex = /^(\d{4})-(\d{2})-(\d{2})/;
    const isoMatch = isoRegex.exec(value);
    if (isoMatch) {
      const year = Number.parseInt(isoMatch[1], 10);
      const month = Number.parseInt(isoMatch[2], 10) - 1;
      const day = Number.parseInt(isoMatch[3], 10);
      const date = new Date(year, month, day);
      if (!Number.isNaN(date.getTime())) {
        return date;
      }
    }

    // Parse slash-delimited values according to the configured display format.
    const slashRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/;
    const slashMatch = slashRegex.exec(value);
    if (slashMatch) {
      const isDayFirst =
        this.dateFormat.indexOf('DD') < this.dateFormat.indexOf('MM');
      const day = Number.parseInt(
        isDayFirst ? slashMatch[1] : slashMatch[2],
        10,
      );
      const month =
        Number.parseInt(isDayFirst ? slashMatch[2] : slashMatch[1], 10) - 1;
      const year = Number.parseInt(slashMatch[3], 10);
      const date = new Date(year, month, day);
      if (!Number.isNaN(date.getTime())) {
        return date;
      }
    }

    // Try native Date parsing as fallback
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }

    return null;
  }

  private initializeFromValue(): void {
    const value = this._value();
    if (!value) {
      return;
    }

    if (this.mode === 'single') {
      this.initializeSingleFromValue(value as Date);
    } else {
      this.initializeRangeFromValue(value as DateRange);
    }
  }

  private initializeSingleFromValue(date: Date): void {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return;
    }
    this.currentMonth.set(date.getMonth());
    this.currentYear.set(date.getFullYear());
    if (this.usesTimePicker()) {
      this.updateTimeForRange('start', date);
    }
  }

  private initializeRangeFromValue(range: DateRange): void {
    if (
      range.start &&
      range.start instanceof Date &&
      !Number.isNaN(range.start.getTime())
    ) {
      this.currentMonth.set(range.start.getMonth());
      this.currentYear.set(range.start.getFullYear());
      if (this.showTime) {
        this.updateTimeForRange('start', range.start);
      }
    }
    if (
      range.end &&
      range.end instanceof Date &&
      !Number.isNaN(range.end.getTime()) &&
      this.showTime
    ) {
      this.updateTimeForRange('end', range.end);
    }
  }

  private updateValue(): void {
    const value = this._value();
    if (this._onChange) {
      this._onChange(value);
    }
    if (value) {
      this.dateChange.emit(value);
    }
  }

  private createDraftDateFromTime(type: 'start' | 'end'): Date {
    const base = this.timeOnly ? new Date(2000, 0, 1) : new Date();
    let hours = type === 'start' ? this.startHour() : this.endHour();
    const minutes = type === 'start' ? this.startMinute() : this.endMinute();
    const seconds = type === 'start' ? this.startSecond() : this.endSecond();
    const amPm = type === 'start' ? this.startAmPm() : this.endAmPm();

    if (this.timeFormat === '12h') {
      hours = amPm === 'PM' ? (hours % 12) + 12 : hours % 12;
    }

    base.setHours(hours, minutes, seconds, 0);
    return base;
  }

  // Hour options for time picker
  getHourOptions(): number[] {
    if (this.timeFormat === '24h') {
      return Array.from({ length: 24 }, (_, i) => i);
    }
    return Array.from({ length: 12 }, (_, i) => i + 1);
  }

  // Minute options for time picker
  getMinuteOptions(): number[] {
    return Array.from({ length: 60 }, (_, i) => i);
  }

  // Time increment/decrement methods
  incrementHour(type: 'start' | 'end'): void {
    const is24h = this.timeFormat === '24h';
    const max = is24h ? 23 : 12;
    const wrapValue = is24h ? 0 : 1;

    if (type === 'start') {
      const current = this.startHour();
      const newValue = current >= max ? wrapValue : current + 1;
      this.startHour.set(newValue);
    } else {
      const current = this.endHour();
      const newValue = current >= max ? wrapValue : current + 1;
      this.endHour.set(newValue);
    }
    this.onTimeChange();
  }

  decrementHour(type: 'start' | 'end'): void {
    const is24h = this.timeFormat === '24h';
    const min = is24h ? 0 : 1;
    const wrapValue = is24h ? 23 : 12;

    if (type === 'start') {
      const current = this.startHour();
      const newValue = current <= min ? wrapValue : current - 1;
      this.startHour.set(newValue);
    } else {
      const current = this.endHour();
      const newValue = current <= min ? wrapValue : current - 1;
      this.endHour.set(newValue);
    }
    this.onTimeChange();
  }

  incrementMinute(type: 'start' | 'end'): void {
    if (type === 'start') {
      const current = this.startMinute();
      const newValue = current >= 59 ? 0 : current + 1;
      this.startMinute.set(newValue);
    } else {
      const current = this.endMinute();
      const newValue = current >= 59 ? 0 : current + 1;
      this.endMinute.set(newValue);
    }
    this.onTimeChange();
  }

  decrementMinute(type: 'start' | 'end'): void {
    if (type === 'start') {
      const current = this.startMinute();
      const newValue = current <= 0 ? 59 : current - 1;
      this.startMinute.set(newValue);
    } else {
      const current = this.endMinute();
      const newValue = current <= 0 ? 59 : current - 1;
      this.endMinute.set(newValue);
    }
    this.onTimeChange();
  }

  incrementSecond(type: 'start' | 'end'): void {
    if (type === 'start') {
      const current = this.startSecond();
      this.startSecond.set(current >= 59 ? 0 : current + 1);
    } else {
      const current = this.endSecond();
      this.endSecond.set(current >= 59 ? 0 : current + 1);
    }
    this.onTimeChange();
  }

  decrementSecond(type: 'start' | 'end'): void {
    if (type === 'start') {
      const current = this.startSecond();
      this.startSecond.set(current <= 0 ? 59 : current - 1);
    } else {
      const current = this.endSecond();
      this.endSecond.set(current <= 0 ? 59 : current - 1);
    }
    this.onTimeChange();
  }

  toggleAmPm(type: 'start' | 'end'): void {
    if (type === 'start') {
      this.startAmPm.set(this.startAmPm() === 'AM' ? 'PM' : 'AM');
    } else {
      this.endAmPm.set(this.endAmPm() === 'AM' ? 'PM' : 'AM');
    }
    this.onTimeChange();
  }

  // Direct input handling methods
  onHourInput(type: 'start' | 'end', event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = Number.parseInt(input.value, 10);

    if (Number.isNaN(value)) {
      return;
    }

    const is24h = this.timeFormat === '24h';
    const min = is24h ? 0 : 1;
    const max = is24h ? 23 : 12;

    if (type === 'start') {
      const clampedValue = Math.max(min, Math.min(max, value));
      this.startHour.set(clampedValue);
    } else {
      const clampedValue = Math.max(min, Math.min(max, value));
      this.endHour.set(clampedValue);
    }
    this.onTimeChange();
  }

  onMinuteInput(type: 'start' | 'end', event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = Number.parseInt(input.value, 10);

    if (Number.isNaN(value)) {
      return;
    }

    const clampedValue = Math.max(0, Math.min(59, value));

    if (type === 'start') {
      this.startMinute.set(clampedValue);
    } else {
      this.endMinute.set(clampedValue);
    }
    this.onTimeChange();
  }

  onSecondInput(type: 'start' | 'end', event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = Number.parseInt(input.value, 10);

    if (Number.isNaN(value)) {
      return;
    }

    const clampedValue = Math.max(0, Math.min(59, value));
    if (type === 'start') {
      this.startSecond.set(clampedValue);
    } else {
      this.endSecond.set(clampedValue);
    }
    this.onTimeChange();
  }

  onHourBlur(type: 'start' | 'end', event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = Number.parseInt(input.value, 10);

    const is24h = this.timeFormat === '24h';
    const min = is24h ? 0 : 1;
    const max = is24h ? 23 : 12;

    if (Number.isNaN(value) || value < min || value > max) {
      if (type === 'start') {
        const current = this.startHour();
        input.value = current.toString().padStart(2, '0');
      } else {
        const current = this.endHour();
        input.value = current.toString().padStart(2, '0');
      }
    } else {
      input.value = value.toString().padStart(2, '0');
    }
  }

  onMinuteBlur(type: 'start' | 'end', event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = Number.parseInt(input.value, 10);

    if (Number.isNaN(value) || value < 0 || value > 59) {
      if (type === 'start') {
        const current = this.startMinute();
        input.value = current.toString().padStart(2, '0');
      } else {
        const current = this.endMinute();
        input.value = current.toString().padStart(2, '0');
      }
    } else {
      input.value = value.toString().padStart(2, '0');
    }
  }

  onSecondBlur(type: 'start' | 'end', event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = Number.parseInt(input.value, 10);

    if (Number.isNaN(value) || value < 0 || value > 59) {
      if (type === 'start') {
        input.value = this.startSecond().toString().padStart(2, '0');
      } else {
        input.value = this.endSecond().toString().padStart(2, '0');
      }
    } else {
      input.value = value.toString().padStart(2, '0');
    }
  }

  // Today and Reset buttons
  selectToday(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (this.showTime) {
      today.setHours(
        this.startHour(),
        this.startMinute(),
        this.startSecond(),
        0,
      );
    }

    if (this.mode === 'single') {
      this._value.set(today);
      this.updateValue();
      this.currentMonth.set(today.getMonth());
      this.currentYear.set(today.getFullYear());
      this.closePopup();
    } else {
      // For range mode, set both start and end to today
      if (this.showTime) {
        const endDate = new Date(today);
        endDate.setHours(this.endHour(), this.endMinute(), this.endSecond(), 0);
        this._value.set({ start: today, end: endDate });
      } else {
        const endDate = new Date(today);
        endDate.setHours(23, 59, 59, 999);
        this._value.set({ start: today, end: endDate });
      }
      this.updateValue();
      this.currentMonth.set(today.getMonth());
      this.currentYear.set(today.getFullYear());
      this.closePopup();
    }
  }

  resetDate(): void {
    this._value.set(null);
    this.updateValue();
    this.closePopup();
  }
}
